import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Admin-facing AI provider row (from GET /admin/providers).
 * Note: `enabled` can be `null` because the column uses `.default(true)`
 * and Drizzle surfaces the default-absent case as null.
 */
export interface AIProvider {
  /**
   * Provider UUID. Added to the projection in T26 so live-wired admin UIs
   * (e.g. AITasksPage) can supply `providerId` when writing task configs via
   * `PUT /admin/tasks/:task` (the server's Zod schema requires a uuid, not
   * `providerName`). Surfaced from the existing `ai_providers.id` column;
   * not a secret.
   */
  id: string
  name: string
  enabled: boolean | null
  keyFingerprint: string | null
  lastRotatedAt: string | null
  lastTestedAt: string | null
  lastTestOk: boolean | null
  lastTestError: string | null
  baseUrlOverride: string | null
}

export interface ListProvidersResponse {
  providers: AIProvider[]
}

export interface RotateProviderKeyResponse {
  keyFingerprint: string
  testOk: boolean
  wallClockMs: number
}

export interface TestProviderResponse {
  ok: boolean
  wallClockMs: number
  error?: string
}

export interface UpdateProviderInput {
  enabled?: boolean
  baseUrlOverride?: string | null
}

/** List all AI providers. */
export function useAIProviders() {
  return useQuery({
    queryKey: ['ai-providers'],
    queryFn: () => api.get<ListProvidersResponse>('/admin/providers'),
    staleTime: 30_000,
  })
}

/** Rotate (replace) a provider's API key. Test-ping runs server-side before the key is persisted. */
export function useRotateProviderKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ name, apiKey }: { name: string; apiKey: string }) =>
      api.put<RotateProviderKeyResponse>(`/admin/providers/${encodeURIComponent(name)}/key`, {
        apiKey,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-providers'] })
      qc.invalidateQueries({ queryKey: ['ai-provider-models'] })
    },
  })
}

/** Run a test-ping against a provider using the currently-stored key. */
export function useTestProvider() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ name, modelId }: { name: string; modelId?: string }) => {
      const qs = modelId ? `?modelId=${encodeURIComponent(modelId)}` : ''
      return api.post<TestProviderResponse>(
        `/admin/providers/${encodeURIComponent(name)}/test${qs}`,
        {},
      )
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-providers'] }),
  })
}

/** Update provider flags — enabled, baseUrlOverride. */
export function useUpdateProvider() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ name, ...body }: { name: string } & UpdateProviderInput) =>
      api.patch<{ ok: true }>(`/admin/providers/${encodeURIComponent(name)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-providers'] }),
  })
}
